/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useEffect,
  useRef
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {areEqual, VariableSizeList as List} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {AlertSeverity, GenericObject} from '@ion/core/src/types/entities';
import {
  isSafari
} from '@ion/core/src/utils/BrowserUtils';
import {EmptyNotifications} from '@ion/dls/src/notifications/EmptyNotifications';
import {NotificationCard} from '@ion/dls/src/notifications/NotificationCard';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme} from '@ion/dls/src/types/types';
import {resetNotifications} from './state';
import ViewEventCta from './ViewEventCta';

export interface FilterNotificationsType {
  checkboxesEnabled: boolean;
  onMarkAllAsRead?: () => void;
  activeNotifications?: Notification[];
  detailsState?: any;
  selectedNotifications?: Notification[];
  emptyTitle?: string;
  readMore?: (notification: Notification) => void;
  readLess?: (notification: Notification) => void;
  markNotificationRead?: (notification: Notification) => void;
  onClearClick?: (notification: Notification) => void;
  onReviewClick?: (notification: Notification) => void;
  handleCheckBoxChange?: (checked: boolean, notification: Notification) => void;
  onExpand?: (expanded: boolean) => void;
  selectedFilter?: GenericObject;
  setCurrentPage?: Function;
  setNotificationIndex?: Function;
  isLargeFleetMode?: boolean;
  loadNextPage?: Function;
  isNextPageLoading?: boolean;
  hasNextPage?: boolean;
  isPhone?: boolean;
  currentPage?: number;
  notificationIndex?: number;
  isSubAccount?: boolean;
}

const panelHeight = 'calc(100vh - 192px)';
const panelHeightMobile = 'calc(100vh - 107px)';

const useStyles = makeStyles((theme: DLSTheme) => ({
  notificationPanelList : {
    overflowX                      : 'hidden',
    overflowY                      : 'auto',
    height                         : panelHeight,
    [theme.breakpoints.down('sm')] : {
      height : isSafari() ? panelHeight : panelHeightMobile
    }
  },
  panelList : {
    overflowY : 'auto',
    height    : (props: {
      isPhone: boolean;
    }) => props.isPhone ? 'null' : 'calc(100vh - 270px) !important',
    [theme.breakpoints.down('sm')] : {
      height         : 'calc(100vh - 290px)',
      overflowY      : 'scroll',
      scrollBehavior : 'smooth'
    }
  }
}));

export const FilterNotifications = ({
  readMore,
  readLess,
  markNotificationRead,
  onClearClick,
  onReviewClick,
  handleCheckBoxChange,
  setNotificationIndex,
  onExpand,
  setCurrentPage,
  notificationIndex,
  isLargeFleetMode,
  loadNextPage,
  isNextPageLoading,
  hasNextPage,
  selectedFilter,
  currentPage = 0,
  isPhone = false,
  checkboxesEnabled = false,
  isSubAccount = false,
  activeNotifications= [],
  detailsState = [],
  selectedNotifications = [],
  emptyTitle = ''
}: FilterNotificationsType) => {
  const classes         = useStyles({isPhone});
  const dispatch        = useDispatch();
  const listRef         = React.createRef<any>();
  const rowHeights      = useRef({});

  /**
   * @private
   *
   * defines individual asset card component
   */
  const NotificationCardItem = ({index, style}: {index: number, style: GenericObject}) => {
    const rowRef = useRef({});
    useEffect(() => {
      const {current}: any = rowRef;
      if (!isEmpty(current)) {
        const childHeight = current?.children[0]?.clientHeight;
        const rowHeight   = current?.clientHeight;
        const height      = childHeight || rowHeight;
        setRowHeight(index, height ? height + 1 : 0);
      }
    }, [rowRef]);

    const notification =  activeNotifications[index];
    const {
      isRead,
      alertConfigurationName,
      severity = AlertSeverity.MEDIUM,
      createdOn,
      uuid,
      assetName,
      alertType
    } = notification;
    const {title: alertTypeTitle} = alertType;
    const matchingClick = detailsState.find(clicked => clicked.id === uuid);
    const isDetailsOpen = matchingClick?.open && !!notification?.details;
    const isLoadingDetails = matchingClick?.loading;
    const isChecked = selectedNotifications?.find(active => active.uuid === uuid);
    const isUrgent = severity === AlertSeverity.CRITICAL;

    return (
      <div key={`notification-card-${index}`} style={style} ref={rowRef}>
        <NotificationCard type={alertTypeTitle}
          text={assetName}
          subText={alertConfigurationName}
          read={isRead}
          timeStamp={createdOn}
          notification={notification}
          isUrgent={isUrgent}
          viewEventCta={<ViewEventCta notification={notification} />}
          onMoreClick={readMore}
          onLessClick={readLess}
          moreInfoLoading={isLoadingDetails}
          showMoreInfo={isDetailsOpen}
          showCheckBox={checkboxesEnabled}
          onCardClick={markNotificationRead}
          onClearClick={onClearClick}
          onReviewClick={onReviewClick}
          onCheckBoxChange={handleCheckBoxChange}
          isChecked={!!isChecked} />
      </div>
    );
  };

  /**
   * @private
   *
   * defines the list item
   */
  const Items = memo(({index, style}: {index: number, style: GenericObject}) => {
    style = {...style, pointerEvents: 'auto'};
    if (isLargeFleetMode) {
      if (isItemLoaded(index)) {
        return (
          <NotificationCardItem index={index} style={style} />
        );
      }
      return null;
    }
    return (
      <NotificationCardItem index={index} style={style} />
    );
  }, areEqual);

  /**
   * @private
   */
  function setRowHeight(index, size) {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = {...rowHeights?.current, [index]: size};
  }

  /**
   * @private
   */
  function getRowHeight(index) {
    const defaultHeight = 260;
    const height        = rowHeights?.current[index];
    return height || defaultHeight;
  }

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = function(index) {
    return !hasNextPage || index < activeNotifications.length;
  };
  const itemCount = hasNextPage ? activeNotifications.length + 1 : activeNotifications.length;

  /* Only load 1 page of items at a time.Pass an empty callback to InfiniteLoader
  * in case it asks us to load more than once.
  */
  const loadMoreItems = isNextPageLoading ? function() {} : loadNextPage;
  if (activeNotifications.length > 0) {
    return (
      <InfiniteLoader isItemLoaded={isItemLoaded}
        itemCount={itemCount}
        loadMoreItems={loadMoreItems}>
        {({onItemsRendered}) => (
          <List className={classes.panelList}
            ref={listRef}
            itemCount={itemCount}
            itemSize={getRowHeight}
            onItemsRendered={onItemsRendered}
            height={800}
            width='100%'>
            {Items}
          </List>
        )}
      </InfiniteLoader>
    );
  }
  return (
    <EmptyNotifications title={emptyTitle}
      desktopHeight={panelHeight}
      mobileHeight={panelHeightMobile} />
  );
};

export default FilterNotifications;
